import { Skeleton } from '@mantine/core'
import { ApexOptions } from 'apexcharts'
import { Suspense, lazy } from 'react'
import type { ApexChartProps } from './ApexChartInner'

const ApexChartInner = lazy(() => import('./ApexChartInner'))

const removeUndefinedOptions = <T,>(value: T): T => {
  if (Array.isArray(value)) {
    return value.map(removeUndefinedOptions) as T
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([, entryValue]) => entryValue !== undefined)
        .map(([key, entryValue]) => [key, removeUndefinedOptions(entryValue)]),
    ) as T
  }

  return value
}

export const ApexChart = ({
  options,
  height,
  width,
  ...props
}: ApexChartProps) => {
  const normalizedHeight = height ?? options?.chart?.height ?? 'auto'
  const normalizedWidth = width ?? options?.chart?.width ?? '100%'
  const sanitizedOptions = removeUndefinedOptions<ApexOptions>({
    ...options,
    chart: {
      ...options?.chart,
      height: normalizedHeight,
      width: normalizedWidth,
    },
  })

  return (
    <Suspense fallback={<Skeleton animate height={height ?? 300} />}>
      <ApexChartInner
        {...props}
        height={normalizedHeight}
        width={normalizedWidth}
        options={sanitizedOptions}
      />
    </Suspense>
  )
}
